import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {WimdStore} from './wimdStore';
import {FormControl, FormGroup} from '@angular/forms';
import {IWimdDate, IWimdFilters} from './wimdInterfaces';
import {Subject, Subscription} from 'rxjs';
import {WimdModalsService} from './wimd-modal/wimd-modals.service';
import {UserSettingsService} from '../../../shared/services/user-settings/user-settings.service';
import {AuthenticationService} from '../../../shared/services/authentication/authentication.service';
import {ProjectService} from '../../../shared/services/project/project.service';
import {filter, map} from 'rxjs/operators';
import {environment} from '../../../../environments/environment';
import {ToastrService} from 'ngx-toastr';


@Component({
  selector: 'app-project-wimd',
  templateUrl: './project-wimd.component.html',
  styleUrls: ['./project-wimd.component.scss']
})
export class ProjectWimdComponent implements OnInit, OnDestroy {

  initialTime = {hour: 0, minute: 0};
  startTime = {...this.initialTime};
  endTime = {...this.initialTime};

  datePickerControl = new FormGroup({
    startDate: new FormControl(''),
    startTime: new FormControl({...this.initialTime}),
    endDate: new FormControl(''),
    endTime: new FormControl({...this.initialTime})
  });

  filters: IWimdFilters = {
    thresholds: ['', '', '']
  };

  directionsList: string[] = [];
  confidenceList = [0, 1, 2, 3, 4];
  confidence: number = null;
  direction: string = null;
  category: string = null;
  wimdConfiguration: Subject<any> = new Subject();
  readonly ALL = 'all';
  readonly DIRECTION = 'direction';
  readonly CONFIDENCE = 'confidence';
  readonly CATEGORY = 'category';
  private projectId: number;
  private subscriptions = new Subscription();


  constructor(
    public wimdStore: WimdStore,
    private modalService: WimdModalsService,
    private activatedRoute: ActivatedRoute,
    private userSettingsService: UserSettingsService,
    private authenticationService: AuthenticationService,
    private projectService: ProjectService,
    private authService: AuthenticationService,
    private toastr: ToastrService
  ) {
  }


  ngOnInit(): void {
    this.projectId = +this.activatedRoute.snapshot.paramMap.get('idProject');
    this.initDatePicker();
    this.subscribeProject();
    this.wimdStore.initAndFetchPassages(this.projectId);
    this.subscribeWimdStore();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.wimdStore.clearCurrentState();
  }


  applyFilters(): void {
    const filters: IWimdFilters = {
      thresholds: this.checkThresholdValues(this.filters.thresholds),
    };
    this.persistThresholds(filters);
  }

  setTime(event: any, timePicker: 'start' | 'end'): void {
    this.datePickerControl.controls[`${timePicker}Time`].setValue(event);
  }

  getPassages(): void {
    const category = this.getCategoryForQuery(this.filters.thresholds.indexOf(this.category), true);
    this.wimdStore.fetchPassages(Object.assign(this.getFormattedDatepickerValue(), {
      confidence: this.confidence,
      direction: this.direction,
      category: category
    }));
  }

  updateQueryFilter(filterType: 'direction' | 'confidence' | 'category', filterValue?: string | number | undefined | null): void {
    this[`${filterType}`] = filterValue;
    this.getPassages();
  }

  getCategoryForQuery(index: number, forQuery = false): string {
    if (this.category !== undefined && this.filters.thresholds[index] !== '' && this.filters.thresholds[index] !== undefined) {
      let categoryAmplitude = '';
      if (index < this.filters.thresholds.length - 1) {
        categoryAmplitude = this.filters.thresholds[index] + '-' + this.filters.thresholds[index + 1];
      } else {
        categoryAmplitude = this.filters.thresholds[this.filters.thresholds.length - 1] + (forQuery ? '-10000' : '-+oo');
      }
      return categoryAmplitude.length > 2 ? categoryAmplitude : undefined;
    }
  }

  reset(): void {
    this.direction = null;
    this.confidence = null;
    this.category = null;
    this.initDatePicker();
    this.getPassages();
  }

  getCSV(): void {
    const date = this.getFormattedDatepickerValue();
    const token = this.authService.getToken();
    window.open(`${environment.apiUrl}${environment.guardedRoute}auth/projects/${this.projectId}/passages/csv?date_start=${date.start}&date_end=${date.end}&token=${token}`);
  }

  private getSubscriptionDetails(id: number) {
    this.subscriptions.add(
      this.projectService.getSubscriptionDetails(id).subscribe(
        {
          next: (subscription: any) => {
            this.directionsList = subscription.cfg_wimd_algorithm.directions;
            this.wimdConfiguration.next(subscription.cfg_wimd_layout.has_media);
          },
          error: (err) => this.handleError(err)
        }
      )
    );
  }

  private subscribeProject() {
    this.subscriptions.add(
      this.projectService.getProject(this.projectId).subscribe({
        next: (project: any) => this.handleProjectResponse(project),
        error: (err) => this.handleError(err)
      })
    );
  }

  private subscribeWimdStore() {
    this.subscriptions.add(
      this.wimdStore.currentState
        .pipe(
          filter(Boolean),
          map((state: any) => state.filters)
        )
        .subscribe(
          {
            next: (filters) => this.setFilters(filters),
            error: (err) => this.handleError(err)
          })
    );
  }

  private handleProjectResponse(project: any): void {
    if (!Array.isArray(project.project_wimd_subscription)) {
      let wimdSubscriptionId = project.project_wimd_subscription;
      this.getSubscriptionDetails(wimdSubscriptionId);
    }
  }

  private handleError(error: any): void {
    console.error('An error occurred:', error);
    this.toastr.error('An error occurred:' + error);
  }

  private persistThresholds(filters: IWimdFilters): void {
    if (this.authenticationService.userHasRoleOsmos()) {
      this.subscriptions.add(
        this.userSettingsService.patchWimdThresholdsSettings({
          wimd_thresholds: {
            low: this.filters.thresholds[0],
            medium: this.filters.thresholds[1],
            high: this.filters.thresholds[2],
          }
        }).subscribe({
          next: () => this.onAfterPersistThresholds(filters),
          error: (err) => this.handleError(err)
        })
      );
    } else {
      this.onAfterPersistThresholds(filters);
    }
  }

  private onAfterPersistThresholds(filters: IWimdFilters): void {
    this.wimdStore.setFilters(filters);
    this.setFilters(filters);
    this.category = this.ALL;
    this.getPassages();
  }

  private getFormattedDatepickerValue(): IWimdDate {
    const start = this.setTimeToDate(this.datePickerControl.controls.startDate.value, this.startTime.hour, this.startTime.minute);
    const end = this.setTimeToDate(this.datePickerControl.controls.endDate.value, this.endTime.hour, this.endTime.minute);
    return {start: start.toISOString(), end: end.toISOString()};
  }

  private setTimeToDate(date: string, hour: number, minute: number): Date {
    const dateObj = new Date(date);
    return new Date(Date.UTC(dateObj.getUTCFullYear(), dateObj.getUTCMonth(), dateObj.getUTCDate(), hour, minute));
  }

  private initDatePicker(): void {
    const startDate = this.wimdStore.getUtcCurrentStartDate();
    const endDate = this.wimdStore.getUtcCurrentDate();

    this.datePickerControl.controls.startDate.setValue(startDate.toISOString());
    this.datePickerControl.controls.endDate.setValue(endDate.toISOString());

    this.startTime = {hour: startDate.getUTCHours(), minute: startDate.getUTCMinutes()};
    this.endTime = {hour: endDate.getUTCHours(), minute: endDate.getUTCMinutes()};
  }

  private checkThresholdValues(thresholds: any[]): any[] {
    if (thresholds?.some(el => el !== '')) {
      thresholds = thresholds.filter(threshold => threshold !== '');
      thresholds.sort((a, b) => a - b);
    }
    return thresholds ?? [];
  }

  private setFilters(filters: IWimdFilters): void {
    this.filters = filters;
  }


}
